import classNames from 'classnames';
import * as React from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { DateRange } from '../../types';
import { formatDateInputValue, getShortMonthName } from '../../utils/dateUtils';

type DatePanelProps = {
  dateRange: DateRange;
  onChange: (dateRange: DateRange) => void;
  onToggleChange: (isExpanded: boolean) => void;
};

const DatePanel = ({
  dateRange,
  onChange,
  onToggleChange,
}: DatePanelProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [startDate, setStartDate] = React.useState<Date | null>(dateRange[0]);
  const [endDate, setEndDate] = React.useState<Date | null>(dateRange[1]);
  const startDateInputRef = React.useRef<HTMLInputElement>(null);
  const endDateInputRef = React.useRef<HTMLInputElement>(null);
  const ref = React.useRef<HTMLDivElement>(null);
  const isValid = (startDate !== null && isNaN(startDate.getTime()) === false) && (endDate !== null && isNaN(endDate.getTime()) === false);

  React.useEffect(() => {
    onToggleChange(isExpanded);
  }, [isExpanded]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  React.useEffect(() => {
    if (startDateInputRef.current) {
      startDateInputRef.current.valueAsDate = startDate;
    }

    if (endDateInputRef.current) {
      endDateInputRef.current.valueAsDate = endDate;
    }
  }, [startDate, endDate]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        return setIsExpanded(false);
      }

      if (event.key === 'Enter') {

        if (isValid) {
          event.preventDefault();
          onChange([startDate, endDate]);
        }

        return setIsExpanded(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isValid, startDate, endDate, onChange]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="uppercase font-bold h-5 flex items-center leading-4"
      >
        {`${getShortMonthName(dateRange[0])}. ${dateRange[0].getFullYear()} - ${getShortMonthName(dateRange[1])}. ${dateRange[1].getFullYear()}`}
        {isExpanded
          ? <FiChevronUp className="w-3.5 h-3.5 ml-2 pointer-events-none" />
          : <FiChevronDown className="w-3.5 h-3.5 ml-2 pointer-events-none" />}
      </button>
      {isExpanded && (
        <div className="absolute bg-white w-60 top-full shadow-xl p-4 rounded-sm text-sm border mt-3">
          <div className="flex items-center">
            <label htmlFor="startDate" className="w-14 mr-2 font-bold uppercase">Start</label>
            <input
              ref={startDateInputRef}
              type="date"
              id="startDate"
              className="border w-full py-0.5 px-1"
              defaultValue={formatDateInputValue(dateRange[0])}
              onChange={(event) => {
                const { valueAsDate } = event.target as HTMLInputElement;

                if (valueAsDate !== null && isNaN(valueAsDate.getTime()) === false) {
                  setStartDate(valueAsDate);
                }
              }}
              onBlur={(event) => {
                const { valueAsDate } = event.target as HTMLInputElement;
                setStartDate(valueAsDate);
              }}
            />
          </div>
          <div className="flex items-center mt-2">
            <label htmlFor="endDate" className="w-14 mr-2 font-bold uppercase">Slut</label>
            <input
              ref={endDateInputRef}
              type="date"
              id="endDate"
              className="border w-full py-0.5 px-1"
              defaultValue={formatDateInputValue(dateRange[1])}
              onChange={(event) => {
                const { valueAsDate } = event.target as HTMLInputElement;

                if (valueAsDate !== null && isNaN(valueAsDate.getTime()) === false) {
                  setEndDate(valueAsDate);
                }
              }}
              onBlur={(event) => {
                const { valueAsDate } = event.target as HTMLInputElement;
                setEndDate(valueAsDate);
              }}
            />
          </div>
          <button
            onClick={() => {
              if (isValid) {
                onChange([startDate, endDate]);
              }
              setIsExpanded(false);
            }}
            className={classNames(
              'w-full py-1.5 bg-blue-500 font-semibold text-white rounded-sm mt-4 active:bg-blue-600',
              isValid === false && 'opacity-50 cursor-default pointer-events-none',
            )}
          >
            Vælg periode
          </button>
        </div>
      )}
    </div>
  );
};

export default DatePanel;
